import EventDisposePic from '@/model/EventClass/EventDisposePic'
import EventDisposeText from '@/model/EventClass/EventDisposeText'
import type EventBase from '@/model/EventClass/EventBase'
import EventButtonDif from '@/model/EventClass/EventButtonDif'
import EventID from '@/model/EventClass/EventID'
import EventMovePic from '@/model/EventClass/EventMovePic'
import EventPicShow from '@/model/EventClass/EventPicShow'
import EventRotatePic from '@/model/EventClass/EventRotatePic'
import EventText from '@/model/EventClass/EventText'
import EventTextDif from '@/model/EventClass/EventTextDif'
import EventTextDifExBase from '@/model/EventClass/EventTextDifExBase'
import EventTextDifEx2, { PersenalSetClass } from '@/model/EventClass/EventTextDifEx2'
import {
  findStructureBlockEndIndex,
  getStructureBlockRange,
  getStructureSelectionContext,
} from '@/events/list/structureRules'

export type PreviewSceneImageState = {
  layerId: number
  path: string
  x: number
  y: number
  zoomX: number
  zoomY: number
  opacity: number
  isMirror: boolean
  rotation: number
  rotationPivot: 'center' | 'top-left' | 'custom'
  rotationPoint: { x: number; y: number } | null
}

export type PreviewSceneOafState = {
  layerId: number
  path: string
  x: number
  y: number
  zoomX: number
  zoomY: number
  opacity: number
  rotation: number
}

export type PreviewSceneTextState = {
  roleName: string
  content: string
  portraitPath: string
  portraitPos: number
  showDialog: boolean
  roleColor: string
}

export type PreviewSceneChoiceItem = {
  text: string
  buttonIndex: number | null
  x: number | null
  y: number | null
  isCenter: boolean
  textX: number
  textY: number
}

export type PreviewSceneChoiceState = {
  originX: number | null
  originY: number | null
  items: PreviewSceneChoiceItem[]
}

export type PreviewSceneImageChoiceItem = {
  choiceIndex: number
  buttonIndex: number
  x: number
  y: number
}

export type PreviewSceneImageChoiceState = {
  items: PreviewSceneImageChoiceItem[]
}

export type WebGLPreviewImageSprite = PreviewSceneImageState & {
  textureUrl: string
}

export type WebGLPreviewDialogSnapshot = {
  roleName: string
  content: string
  portraitUrl: string
  portraitPos: number
  showDialog: boolean
  roleColor: string
  talkBoxUrl: string
  talkBoxX: number
  talkBoxBottom: number
  talkTextOffsetX: number
  talkTextOffsetY: number
  talkTextWidth: number
  talkTextHeight: number
  nameBoxUrl: string
  nameBoxOffsetX: number
  nameBoxOffsetY: number
  nameTextOffsetX: number
  nameTextOffsetY: number
  fontSize: number
  messageColor: string
  nameCenter: boolean
}

export type WebGLPreviewSnapshot = {
  designWidth: number
  designHeight: number
  images: WebGLPreviewImageSprite[]
  dialog: WebGLPreviewDialogSnapshot | null
  version: string
}

export type PreviewSceneState =
  | {
      kind: 'empty'
      reason: 'invalid-selection' | 'unsupported-selection' | 'no-visual-state'
      images: []
      text: null
      textChoices: null
      imageChoices: null
    }
  | {
      kind: 'scene'
      reason: 'ok'
      images: PreviewSceneImageState[]
      text: PreviewSceneTextState | null
      textChoices: PreviewSceneChoiceState | null
      imageChoices: PreviewSceneImageChoiceState | null
    }

export type PreviewEventListAItem =
  | {
      kind: 'text'
      text: PreviewSceneTextState
    }
  | {
      kind: 'image'
      image: PreviewSceneImageState
    }
  | {
      kind: 'text-choices'
      choices: PreviewSceneChoiceState
    }
  | {
      kind: 'image-choices'
      choices: PreviewSceneImageChoiceState
    }

export type PreviewEventListAResult =
  | {
      kind: 'empty'
      reason: 'invalid-selection' | 'unsupported-selection' | 'no-visual-state'
      items: []
    }
  | {
      kind: 'scene'
      reason: 'ok'
      items: PreviewEventListAItem[]
    }

export type PreviewEditableImageCapabilities = {
  canMove: boolean
  canScale: boolean
  canRotate: boolean
  canEditPivot: boolean
}

export type PreviewEditableImageTarget = {
  kind: 'image'
  sourceEventCode: EventID.PicShow | EventID.MovePic | EventID.RotatePic
  sourceEventIndex: number
  image: PreviewSceneImageState
  originalPath: string
  capabilities: PreviewEditableImageCapabilities
}

export type PreviewEditableImageChoiceTarget = {
  kind: 'image-choices'
  headerIndex: number
  sourceEventIndex: number
  items: PreviewSceneImageChoiceItem[]
}

export type PreviewEditableTarget =
  | {
      kind: 'none'
      reason: 'invalid-selection' | 'unsupported-selection' | 'no-visual-state'
    }
  | PreviewEditableImageTarget
  | PreviewEditableImageChoiceTarget

let lastPreviewSceneStateCache: [EventBase[], number | null | undefined, number, PreviewSceneState] | null = null

function emptyState(reason: Exclude<PreviewSceneState['reason'], 'ok'>): PreviewSceneState {
  return {
    kind: 'empty',
    reason,
    images: [],
    text: null,
    textChoices: null,
    imageChoices: null,
  }
}

function emptyListA(reason: Exclude<PreviewEventListAResult['reason'], 'ok'>): PreviewEventListAResult {
  return {
    kind: 'empty',
    reason,
    items: [],
  }
}

function toInt(value: unknown, fallback = 0): number {
  const next = Number.parseInt(String(value ?? ''), 10)
  return Number.isFinite(next) ? next : fallback
}

function toOpacity255(value: unknown): number {
  const next = toInt(value, 255)
  return Math.max(0, Math.min(255, next))
}

function toZoomPercent(value: unknown): number {
  const next = Number(value)
  if (!Number.isFinite(next)) return 100
  return next > 0 ? next : 100
}

function getIndent(event: EventBase | null | undefined): number {
  return Math.max(0, event?.Indent ?? 0)
}

function isTextPreviewEvent(code: EventID): boolean {
  return code === EventID.Text || code === EventID.DisposeText
}

function isTextChoicePreviewEvent(code: EventID): boolean {
  return (
    code === EventID.TextDif
    || code === EventID.TextDifEx
    || code === EventID.TextDifEx2
    || code === EventID.TextChoice
    || code === EventID.TextEnd
  )
}

function isImageChoicePreviewEvent(code: EventID): boolean {
  return code === EventID.ButtonDif || code === EventID.ButtonDifChoose || code === EventID.ButtonDifEnd
}

function isImagePreviewEvent(code: EventID): boolean {
  return (
    code === EventID.PicShow
    || code === EventID.MovePic
    || code === EventID.RotatePic
    || code === EventID.DisposePic
  )
}

function parseRotationPoint(raw: string): { x: number; y: number } | null {
  const [x, y] = String(raw || '')
    .split(',')
    .map((item) => item.trim())
  if (x == null || y == null) return null
  return { x: toInt(x, 0), y: toInt(y, 0) }
}

function buildPicBaseState(
  layerId: number,
  event: EventPicShow | EventMovePic,
): PreviewSceneImageState {
  return {
    layerId,
    path: String(event.PicPath || '').trim(),
    x: toInt(event.X, 0),
    y: toInt(event.Y, 0),
    zoomX: toZoomPercent(event.ZoomX),
    zoomY: toZoomPercent(event.ZoomY),
    opacity: toOpacity255(event.Opacity),
    isMirror: Boolean(event.IsMirror),
    rotation: 0,
    rotationPivot: 'center',
    rotationPoint: null,
  }
}

function buildRotateOnlyState(layerId: number, event: EventRotatePic): PreviewSceneImageState {
  const rotation = buildPicRotationState(event)
  return {
    layerId,
    path: '',
    x: 0,
    y: 0,
    zoomX: 100,
    zoomY: 100,
    opacity: 255,
    isMirror: false,
    rotation: rotation.rotation,
    rotationPivot: rotation.rotationPivot,
    rotationPoint: rotation.rotationPoint,
  }
}

function buildPicRotationState(event: EventRotatePic): {
  rotation: number
  rotationPivot: 'center' | 'top-left' | 'custom'
  rotationPoint: { x: number; y: number } | null
} {
  const rotation = toInt(event.RationAngle, 0)
  if (event.PointRate === 1) {
    return {
      rotation,
      rotationPivot: 'top-left',
      rotationPoint: { x: 0, y: 0 },
    }
  }
  if (event.PointRate === 10) {
    return {
      rotation,
      rotationPivot: 'custom',
      rotationPoint: parseRotationPoint(event.RotationPoint),
    }
  }
  return {
    rotation,
    rotationPivot: 'center',
    rotationPoint: null,
  }
}

function buildTextState(event: EventText): PreviewSceneTextState {
  return {
    roleName: String(event.RoleName || '').trim(),
    content: String(event.Content ?? ''),
    portraitPath: String(event.Portrait || '').trim(),
    portraitPos: toInt(event.PortraitPos, 0),
    showDialog: Boolean(event.ShowDialog),
    roleColor: String(event.RoleColor || '').trim(),
  }
}

function findContainingHeaderIndex(
  events: EventBase[],
  selectedIndex: number,
  headerCode: EventID,
): number {
  for (let index = selectedIndex; index >= 0; index -= 1) {
    const event = events[index]
    if (!event || event.Code !== headerCode) continue
    const endIndex = findStructureBlockEndIndex(events, index)
    if (selectedIndex >= index && selectedIndex <= endIndex) {
      return index
    }
  }
  return -1
}

function findContainingHeaderIndexByCodes(
  events: EventBase[],
  selectedIndex: number,
  headerCodes: readonly EventID[],
): number {
  for (let index = selectedIndex; index >= 0; index -= 1) {
    const event = events[index]
    if (!event || !headerCodes.includes(event.Code)) continue
    const endIndex = findStructureBlockEndIndex(events, index)
    if (selectedIndex >= index && selectedIndex <= endIndex) {
      return index
    }
  }
  return -1
}

function getHeaderTextChoices(header: EventBase): string[] {
  const source = header as EventBase & {
    TextDifChoice?: string[]
    ChoiceText?: string[]
  }
  if (Array.isArray(source.TextDifChoice) && source.TextDifChoice.length) {
    return source.TextDifChoice.map((item) => String(item ?? ''))
  }
  if (Array.isArray(source.ChoiceText) && source.ChoiceText.length) {
    return source.ChoiceText.map((item) => String(item ?? ''))
  }
  return []
}

function buildTextChoiceItems(header: EventBase, texts: string[]): PreviewSceneChoiceState {
  if ((header instanceof EventTextDifEx2 || header.Code === EventID.TextDifEx2) && (header as EventTextDifEx2).IsCustomOpen) {
    const personalSet = (header as EventTextDifEx2).PersonalSet ?? []
    return {
      originX: null,
      originY: null,
      items: texts.map((text, index) => {
        const item = personalSet[index] ?? new PersenalSetClass()
        return {
          text,
          buttonIndex: Number.isFinite(item.ButtonIndex) ? item.ButtonIndex : null,
          x: Number.isFinite(item.AbsButtonX) ? item.AbsButtonX : null,
          y: Number.isFinite(item.AbsButtonY) ? item.AbsButtonY : null,
          isCenter: Boolean(item.IsCenter),
          textX: Number.isFinite(item.RETextX) ? item.RETextX : 0,
          textY: Number.isFinite(item.RETextY) ? item.RETextY : 0,
        }
      }),
    }
  }

  const baseHeader = header as EventTextDifExBase
  const hasPosition = header.Code === EventID.TextDifEx || header.Code === EventID.TextDifEx2
  const isCenter = hasPosition ? Boolean(baseHeader.IsCenter) : true
  const useOrigin = hasPosition && !isCenter
  return {
    originX: useOrigin && Number.isFinite(baseHeader.X) ? baseHeader.X : null,
    originY: useOrigin && Number.isFinite(baseHeader.Y) ? baseHeader.Y : null,
    items: texts.map((text) => ({
      text,
      buttonIndex: null,
      x: null,
      y: null,
      isCenter,
      textX: 0,
      textY: 0,
    })),
  }
}

function buildTextChoiceState(
  events: EventBase[],
  selectedIndex: number,
): PreviewSceneChoiceState | null {
  const headerIndex = findContainingHeaderIndexByCodes(
    events,
    selectedIndex,
    [EventID.TextDif, EventID.TextDifEx, EventID.TextDifEx2],
  )
  if (headerIndex < 0) return null

  const header = events[headerIndex] as EventTextDif
  const range = getStructureBlockRange(events, headerIndex)
  const baseIndent = range?.headerIndent ?? header.Indent ?? 0
  const optionIndent = range?.optionIndent ?? baseIndent + 1
  const endIndex = range?.endIndex ?? findStructureBlockEndIndex(events, headerIndex)
  const texts: string[] = []
  for (let index = headerIndex + 1; index <= endIndex; index += 1) {
    const event = events[index]
    if (!event) continue
    const indent = getIndent(event)
    if (event.Code === EventID.TextChoice && (indent === optionIndent || indent === baseIndent)) {
      texts.push(String(event.Parameter?.[1] ?? ''))
    }
  }

  return buildTextChoiceItems(header, texts.length ? texts : getHeaderTextChoices(header))
}

function buildImageChoiceState(
  events: EventBase[],
  selectedIndex: number,
): PreviewSceneImageChoiceState | null {
  const headerIndex = findContainingHeaderIndex(events, selectedIndex, EventID.ButtonDif)
  if (headerIndex < 0) return null

  const header = events[headerIndex] as EventButtonDif
  const items = (header.ButtonChoice ?? [])
    .map((item) => String(item ?? '').split(','))
    .map((parts, choiceIndex) => ({
      choiceIndex,
      buttonIndex: toInt(parts[0], 0),
      x: toInt(parts[1], 0),
      y: toInt(parts[2], 0),
    }))
  return items.length ? { items } : null
}

type PreviewBranchContext = {
  headerIndex: number
  optionIndex: number
  endIndex: number
}

function isPreviewBranchOption(
  code: number,
  indent: number,
  optionCodes: readonly EventID[],
  optionIndent: number,
  headerIndent: number,
): boolean {
  return optionCodes.includes(code as EventID) && (indent === optionIndent || indent === headerIndent)
}

/**
 * 收集当前选中事件所在的所有结构分支。
 * @description 预览回溯需要同时避开外层图片/文本选项的兄弟分支，以及内层条件分歧的兄弟分支。
 */
function getPreviewBranchContexts(events: EventBase[], selectedIndex: number): PreviewBranchContext[] {
  const contexts: PreviewBranchContext[] = []
  for (let headerIndex = 0; headerIndex < selectedIndex; headerIndex += 1) {
    const range = getStructureBlockRange(events, headerIndex)
    if (!range || selectedIndex > range.endIndex || !range.rule.options.length) continue
    for (let optionIndex = headerIndex + 1; optionIndex <= range.endIndex; optionIndex += 1) {
      const optionEvent = events[optionIndex]
      if (!optionEvent) continue
      const optionEventIndent = getIndent(optionEvent)
      if (
        !isPreviewBranchOption(
          optionEvent.Code,
          optionEventIndent,
          range.rule.options,
          range.optionIndent,
          range.headerIndent,
        )
      ) {
        continue
      }
      let branchEnd = range.endIndex
      for (let nextIndex = optionIndex + 1; nextIndex <= range.endIndex; nextIndex += 1) {
        const nextEvent = events[nextIndex]
        if (!nextEvent) continue
        const nextIndent = getIndent(nextEvent)
        if (
          isPreviewBranchOption(
            nextEvent.Code,
            nextIndent,
            range.rule.options,
            range.optionIndent,
            range.headerIndent,
          )
          || (nextIndent === range.headerIndent && range.rule.ends.includes((nextEvent.Code ?? -1) as EventID))
        ) {
          branchEnd = nextIndex - 1
          break
        }
      }
      if (selectedIndex > optionIndex && selectedIndex <= branchEnd) {
        contexts.push({ headerIndex, optionIndex, endIndex: range.endIndex })
        break
      }
    }
  }
  return contexts
}

export function collectPreviewEventListA(
  events: EventBase[],
  selectedIndex: number | null | undefined,
): PreviewEventListAResult {
  if (selectedIndex == null || selectedIndex < 0 || selectedIndex >= events.length) {
    return emptyListA('invalid-selection')
  }
  const selectedEvent = events[selectedIndex]
  if (!selectedEvent) {
    return emptyListA('unsupported-selection')
  }

  const backwardItems: PreviewEventListAItem[] = []
  const selectedSupportsTextChoices = isTextChoicePreviewEvent(selectedEvent.Code)
  const selectedSupportsImageChoices = isImageChoicePreviewEvent(selectedEvent.Code)
  const textChoices = selectedSupportsTextChoices ? buildTextChoiceState(events, selectedIndex) : null
  const imageChoices = selectedSupportsImageChoices ? buildImageChoiceState(events, selectedIndex) : null
  let textCaptured = false
  let textDisposed = false
  let textChoiceCaptured = false
  let imageChoiceCaptured = false
  const hiddenLayerIds = new Set<number>()
  const imageStates = new Map<number, PreviewSceneImageState>()
  const imageOriginKinds = new Map<number, 'show' | 'move' | 'rotate'>()
  const imageBaseResolved = new Set<number>()
  const structureContext = getStructureSelectionContext(events, selectedIndex)
  const branchContexts = getPreviewBranchContexts(events, selectedIndex)
  const choiceBlockContext = structureContext.kind === 'none' ? null : structureContext
  const previewBlockHeaderIndex = branchContexts[0]?.headerIndex ?? choiceBlockContext?.headerIndex ?? -1
  const selectedIndent = getIndent(selectedEvent)
  let indent = selectedIndent

  for (let index = selectedIndex; index >= 0; index -= 1) {
    const event = events[index]
    if (!event) continue
    const eventIndent = getIndent(event)
    if (eventIndent > selectedIndent) {
      continue
    }
    const isOutsideSelectedBranchPath = branchContexts.some((context) =>
      index > context.headerIndex
      && index <= context.endIndex
      && !(index > context.optionIndex && index <= selectedIndex),
    )
    if (isOutsideSelectedBranchPath) {
      continue
    }
    if (
      choiceBlockContext
      && !branchContexts.length
      && index > choiceBlockContext.headerIndex
      && index <= choiceBlockContext.endIndex
      && !isTextChoicePreviewEvent(event.Code)
      && !isImageChoicePreviewEvent(event.Code)
    ) {
      continue
    }
    if (previewBlockHeaderIndex >= 0 && index < previewBlockHeaderIndex) {
      indent = eventIndent
    } else if (eventIndent < indent) {
      continue
    }
    indent = eventIndent

    if (selectedSupportsTextChoices && isTextChoicePreviewEvent(event.Code)) {
      if (!textChoiceCaptured && textChoices?.items.length) {
        backwardItems.push({
          kind: 'text-choices',
          choices: textChoices,
        })
        textChoiceCaptured = true
      }
      continue
    }

    if (selectedSupportsImageChoices && isImageChoicePreviewEvent(event.Code)) {
      if (!imageChoiceCaptured && imageChoices?.items.length) {
        backwardItems.push({
          kind: 'image-choices',
          choices: imageChoices,
        })
        imageChoiceCaptured = true
      }
      continue
    }

    if (!selectedSupportsTextChoices && isTextChoicePreviewEvent(event.Code)) {
      continue
    }

    if (!selectedSupportsImageChoices && isImageChoicePreviewEvent(event.Code)) {
      continue
    }

    if (event instanceof EventDisposeText || event.Code === EventID.DisposeText) {
      if (!textCaptured) textDisposed = true
      continue
    }

    if ((event instanceof EventText || event.Code === EventID.Text) && !textCaptured && !textDisposed) {
      backwardItems.push({
        kind: 'text',
        text: buildTextState(event as EventText),
      })
      textCaptured = true
      continue
    }

    if (event instanceof EventDisposePic || event.Code === EventID.DisposePic) {
      const layerId = Math.max(0, toInt((event as EventDisposePic).PicId, 0))
      if (layerId > 0) {
        hiddenLayerIds.add(layerId)
      }
      continue
    }

    if (event instanceof EventRotatePic || event.Code === EventID.RotatePic) {
      const rotateEvent = event as EventRotatePic
      const layerId = Math.max(0, toInt(rotateEvent.PicId, 0))
      if (layerId > 0 && !hiddenLayerIds.has(layerId) && !imageStates.has(layerId)) {
        const imageState = buildRotateOnlyState(layerId, rotateEvent)
        backwardItems.push({
          kind: 'image',
          image: imageState,
        })
        imageStates.set(layerId, imageState)
        imageOriginKinds.set(layerId, 'rotate')
      }
      continue
    }

    if (event instanceof EventMovePic || event.Code === EventID.MovePic) {
      const moveEvent = event as EventMovePic
      const layerId = Math.max(0, toInt(moveEvent.PicId, 0))
      if (layerId > 0 && !hiddenLayerIds.has(layerId) && !imageStates.has(layerId)) {
        const imageState = buildPicBaseState(layerId, moveEvent)
        backwardItems.push({
          kind: 'image',
          image: imageState,
        })
        imageStates.set(layerId, imageState)
        imageOriginKinds.set(layerId, 'move')
        imageBaseResolved.add(layerId)
      } else if (layerId > 0 && !hiddenLayerIds.has(layerId)) {
        const existing = imageStates.get(layerId)
        const originKind = imageOriginKinds.get(layerId)
        if (!existing) {
          continue
        }
        if (originKind === 'rotate' && !imageBaseResolved.has(layerId)) {
          const baseState = buildPicBaseState(layerId, moveEvent)
          existing.path = baseState.path
          existing.x = baseState.x
          existing.y = baseState.y
          existing.zoomX = baseState.zoomX
          existing.zoomY = baseState.zoomY
          existing.opacity = baseState.opacity
          existing.isMirror = baseState.isMirror
          imageBaseResolved.add(layerId)
        } else if (!existing.path) {
          const fallbackPath = String(moveEvent.PicPath || '').trim()
          if (fallbackPath) {
            existing.path = fallbackPath
          }
        }
      }
      continue
    }

    if (event instanceof EventPicShow || event.Code === EventID.PicShow) {
      const picEvent = event as EventPicShow
      const layerId = Math.max(0, toInt(picEvent.PicId, 0))
      if (layerId > 0 && !hiddenLayerIds.has(layerId) && !imageStates.has(layerId)) {
        const imageState = buildPicBaseState(layerId, picEvent)
        backwardItems.push({
          kind: 'image',
          image: imageState,
        })
        imageStates.set(layerId, imageState)
        imageOriginKinds.set(layerId, 'show')
        imageBaseResolved.add(layerId)
      } else if (layerId > 0 && !hiddenLayerIds.has(layerId)) {
        const existing = imageStates.get(layerId)
        const originKind = imageOriginKinds.get(layerId)
        if (!existing) {
          continue
        }
        if (originKind === 'rotate' && !imageBaseResolved.has(layerId)) {
          const baseState = buildPicBaseState(layerId, picEvent)
          existing.path = baseState.path
          existing.x = baseState.x
          existing.y = baseState.y
          existing.zoomX = baseState.zoomX
          existing.zoomY = baseState.zoomY
          existing.opacity = baseState.opacity
          existing.isMirror = baseState.isMirror
          imageBaseResolved.add(layerId)
        } else if (!existing.path) {
          const fallbackPath = String(picEvent.PicPath || '').trim()
          if (fallbackPath) {
            existing.path = fallbackPath
          }
        }
      }
    }
  }

  const items = backwardItems.reverse()
  if (!items.length) {
    return emptyListA('no-visual-state')
  }
  return {
    kind: 'scene',
    reason: 'ok',
    items,
  }
}

export function buildPreviewSceneState(
  events: EventBase[],
  selectedIndex: number | null | undefined,
  revision?: number,
): PreviewSceneState {
  if (
    revision !== undefined
    && lastPreviewSceneStateCache
    && lastPreviewSceneStateCache[0] === events
    && lastPreviewSceneStateCache[1] === selectedIndex
    && lastPreviewSceneStateCache[2] === revision
  ) {
    return lastPreviewSceneStateCache[3]
  }
  const listA = collectPreviewEventListA(events, selectedIndex)
  if (listA.kind !== 'scene') {
    const state = emptyState(listA.reason)
    if (revision !== undefined) lastPreviewSceneStateCache = [events, selectedIndex, revision, state]
    return state
  }

  let text: PreviewSceneTextState | null = null
  let textChoices: PreviewSceneChoiceState | null = null
  let imageChoices: PreviewSceneImageChoiceState | null = null
  const images = new Map<number, PreviewSceneImageState>()

  for (const item of listA.items) {
    if (item.kind === 'text') {
      text = item.text
      continue
    }
    if (item.kind === 'image') {
      images.set(item.image.layerId, item.image)
      continue
    }
    if (item.kind === 'text-choices') {
      textChoices = item.choices
      continue
    }
    imageChoices = item.choices
  }

  const imageList = [...images.values()].sort((a, b) => a.layerId - b.layerId)
  if (!text && !textChoices && !imageChoices && !imageList.length) {
    const state = emptyState('no-visual-state')
    if (revision !== undefined) lastPreviewSceneStateCache = [events, selectedIndex, revision, state]
    return state
  }

  const state: PreviewSceneState = {
    kind: 'scene',
    reason: 'ok',
    images: imageList,
    text,
    textChoices,
    imageChoices,
  }
  if (revision !== undefined) lastPreviewSceneStateCache = [events, selectedIndex, revision, state]
  return state
}

export function buildEditablePreviewTarget(
  events: EventBase[],
  selectedIndex: number | null | undefined,
  revision?: number,
): PreviewEditableTarget {
  if (selectedIndex == null || selectedIndex < 0 || selectedIndex >= events.length) {
    return { kind: 'none', reason: 'invalid-selection' }
  }
  const selectedEvent = events[selectedIndex]
  if (!selectedEvent) {
    return { kind: 'none', reason: 'unsupported-selection' }
  }

  if (isImageChoicePreviewEvent(selectedEvent.Code)) {
    const headerIndex = findContainingHeaderIndex(events, selectedIndex, EventID.ButtonDif)
    if (headerIndex < 0) return { kind: 'none', reason: 'unsupported-selection' }
    const imageChoices = buildImageChoiceState(events, selectedIndex)
    if (!imageChoices?.items.length) {
      return { kind: 'none', reason: 'no-visual-state' }
    }
    return {
      kind: 'image-choices',
      headerIndex,
      sourceEventIndex: selectedIndex,
      items: imageChoices.items,
    }
  }

  if (!isImagePreviewEvent(selectedEvent.Code)) {
    return { kind: 'none', reason: 'unsupported-selection' }
  }

  const scene = buildPreviewSceneState(events, selectedIndex, revision)
  if (scene.kind !== 'scene') {
    return { kind: 'none', reason: scene.reason }
  }

  const layerId = Math.max(
    0,
    toInt(
      selectedEvent.Code === EventID.RotatePic
        ? (selectedEvent as EventRotatePic).PicId
        : selectedEvent.Code === EventID.MovePic
          ? (selectedEvent as EventMovePic).PicId
          : (selectedEvent as EventPicShow).PicId,
      0,
    ),
  )
  const image = scene.images.find((item) => item.layerId === layerId)
  if (!image) {
    return { kind: 'none', reason: 'no-visual-state' }
  }

  if (selectedEvent.Code === EventID.PicShow) {
    return {
      kind: 'image',
      sourceEventCode: EventID.PicShow,
      sourceEventIndex: selectedIndex,
      image,
      originalPath: image.path,
      capabilities: {
        canMove: true,
        canScale: true,
        canRotate: false,
        canEditPivot: false,
      },
    }
  }

  if (selectedEvent.Code === EventID.MovePic) {
    const moveEvent = selectedEvent as EventMovePic
    return {
      kind: 'image',
      sourceEventCode: EventID.MovePic,
      sourceEventIndex: selectedIndex,
      image,
      originalPath: image.path,
      capabilities: {
        canMove: !moveEvent.IsAssigned,
        canScale: true,
        canRotate: false,
        canEditPivot: false,
      },
    }
  }

  const rotateEvent = selectedEvent as EventRotatePic
  return {
    kind: 'image',
    sourceEventCode: EventID.RotatePic,
    sourceEventIndex: selectedIndex,
    image,
    originalPath: image.path,
    capabilities: {
      canMove: false,
      canScale: false,
      canRotate: rotateEvent.RationAngleValueOrNot === 0,
      canEditPivot: rotateEvent.PointRate === 10,
    },
  }
}
